/**
 * Perioade uzuale, ca interval de date.
 *
 * Ecranele filtreaza aproape intotdeauna pe luna sau pe an, iar scrierea manuala a doua date
 * pentru „luna trecuta" e o operatie de fiecare zi. Aici sunt calculate o singura data.
 *
 * Doua capcane, amandoua ocolite deliberat:
 *
 * ORA LOCALA, nu UTC. `toISOString()` intoarce ziua in UTC, iar in Romania (UTC+2/+3) asta
 * muta o data de 1 ale lunii cu o zi inapoi — filtrul ar incepe pe 31 ale lunii precedente.
 * Zilele se compun din `getFullYear/getMonth/getDate`, care sunt locale.
 *
 * TRECEREA ANULUI. Luna precedenta lui ianuarie e decembrie ANUL TRECUT. Constructorul `Date`
 * trece corect peste granita cand ii dai luna -1, dar numai daca nu incerci sa „ajuti" cu
 * aritmetica pe numarul lunii.
 */

export type NumePerioada = 'luna_curenta' | 'luna_precedenta' | 'an_curent' | 'an_precedent'

export interface Interval {
  from: string
  to: string
}

/** `YYYY-MM-DD` din componentele LOCALE ale datei — vezi nota despre UTC de mai sus. */
export function ziLocala(d: Date): string {
  const luna = String(d.getMonth() + 1).padStart(2, '0')
  const zi = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${luna}-${zi}`
}

/**
 * Intervalul unei perioade, fata de `acum` (implicit, momentul apelului).
 *
 * `acum` e parametru ca sa poata fi testat: o functie care citeste singura ceasul nu se poate
 * verifica pe 31 decembrie fara sa astepti pana pe 31 decembrie.
 */
export function interval(nume: NumePerioada, acum: Date = new Date()): Interval {
  const an = acum.getFullYear()
  const luna = acum.getMonth()

  switch (nume) {
    case 'luna_curenta':
      // Ziua 0 din luna urmatoare = ultima zi a lunii curente. Merge si pentru februarie
      // bisect, fara sa stim cate zile are luna.
      return { from: ziLocala(new Date(an, luna, 1)), to: ziLocala(new Date(an, luna + 1, 0)) }
    case 'luna_precedenta':
      // `luna - 1` poate fi -1; constructorul trece singur in decembrie anul trecut.
      return { from: ziLocala(new Date(an, luna - 1, 1)), to: ziLocala(new Date(an, luna, 0)) }
    case 'an_curent':
      return { from: `${an}-01-01`, to: `${an}-12-31` }
    case 'an_precedent':
      return { from: `${an - 1}-01-01`, to: `${an - 1}-12-31` }
  }
}

/** Eticheta scurta pentru butoane. */
export const ETICHETE_PERIOADA: Record<NumePerioada, string> = {
  luna_curenta: 'Luna curentă',
  luna_precedenta: 'Luna precedentă',
  an_curent: 'Anul curent',
  an_precedent: 'Anul precedent',
}

const NUME_LUNI = [
  'ianuarie',
  'februarie',
  'martie',
  'aprilie',
  'mai',
  'iunie',
  'iulie',
  'august',
  'septembrie',
  'octombrie',
  'noiembrie',
  'decembrie',
]

/** `2026.07` -> `iulie 2026`. Perioada necunoscuta se intoarce asa cum a venit. */
export function lunaInCuvinte(period: string): string {
  const [an, luna] = period.split('.').map(Number)
  const nume = NUME_LUNI[(luna ?? 0) - 1]
  return nume && an ? `${nume} ${an}` : period
}

/**
 * Ultimele `cate` luni in format `YYYY.MM`, cea curenta prima.
 *
 * Pentru alegerea lunii pe care se muta un bon. Merge si inapoi peste granita anului — vezi
 * nota de sus despre constructorul `Date`.
 */
export function luniRecente(cate = 18, acum: Date = new Date()): string[] {
  const luni: string[] = []
  for (let i = 0; i < cate; i++) {
    const d = new Date(acum.getFullYear(), acum.getMonth() - i, 1)
    luni.push(`${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return luni
}

/**
 * `2026-08-22` → `2026.08`. Ziua dintr-un camp de data, ca luna unui decont.
 *
 * Taierea se face pe TEXT, nu prin `new Date()`: un `Date` construit dintr-un ISO fara ora e
 * interpretat ca UTC si, citit local, poate sari in luna precedenta. Aici nu se converteste
 * nimic, doar se citesc primele sapte caractere.
 */
export function lunaDinZiISO(zi: string): string {
  return /^\d{4}-\d{2}/.test(zi) ? zi.slice(0, 7).replace('-', '.') : ''
}
